'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_BOOTSTRAP_CONCURRENCY, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_COMPLETE_DESKTOP_CONCURRENCY, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_COMPLETE_LOAD_TIMEOUT_MS, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_COMPLETE_MOBILE_CONCURRENCY, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_CONCURRENCY, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_IDLE_CONCURRENCY, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_INTERACTIVE_FRAME_QUALITY, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_LOAD_TIMEOUT_MS, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MAX_CACHED_FRAME_COUNT, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MAX_HIGH_PRIORITY_REQUESTS, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MAX_RETRIES, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MOBILE_CONCURRENCY, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MOBILE_MAX_CACHED_FRAME_COUNT, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_PREVIEW_CONCURRENCY, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_SHARP_FRAME_QUALITY } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/constants';
import { getNearestReadyIndex, getThreesixtyCompleteSequenceRequestWidth, getThreesixtyFrameRequestWidth, getThreesixtyLoadRank, getThreesixtyStoppedFrameRequestWidth, hasReadyFrameWindow, hasReadyPreviewFrames, isThreesixtyAbortError, isThreesixtyPrefetchFrame, loadThreesixtyFrame, trimThreesixtyFrameCache } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/loader-utils';
import { getThreesixtyPreviewFrameIndexes, getThreesixtyPreviewPlaybackFrameIndexes, getThreesixtyPreviewStartFrameIndex } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/preview/utils';
import type { SpartanProductExteriorThreesixtyFrameCacheSnapshot, SpartanProductExteriorThreesixtyLoader, SpartanProductExteriorThreesixtyLoadMode, SpartanProductExteriorThreesixtyLoadState, SpartanProductExteriorThreesixtyRotationDirection } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/types';
import { wrapThreesixtyIndex } from '@/app/lcv/components/sections/spartan/products/exterior/threesixty/utils';
interface UseThreesixtyLoaderOptions {
  getInitialFrameImages?: () => SpartanProductExteriorThreesixtyFrameCacheSnapshot;
  isInteractiveBoosted?: boolean;
  isPreviewWarmupOnly?: boolean;
  mode?: SpartanProductExteriorThreesixtyLoadMode;
  paused?: boolean;
  requireCompleteSequence?: boolean;
}
const getCurrentFrameRequestWidth = (requireCompleteSequence: boolean) => {
  if (typeof window === 'undefined') {
    return 1280;
  }
  return requireCompleteSequence ? getThreesixtyCompleteSequenceRequestWidth(window.innerWidth, window.devicePixelRatio) : getThreesixtyFrameRequestWidth(window.innerWidth, window.devicePixelRatio);
};
const getInteractiveConcurrency = (isBoosted: boolean, requireCompleteSequence: boolean) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  if (requireCompleteSequence) {
    return isMobile ? SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_COMPLETE_MOBILE_CONCURRENCY : SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_COMPLETE_DESKTOP_CONCURRENCY;
  }
  if (!isBoosted) {
    return isMobile ? SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MOBILE_CONCURRENCY : SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_BOOTSTRAP_CONCURRENCY;
  }
  return isMobile ? SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MOBILE_CONCURRENCY : SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_CONCURRENCY;
};
const getMaxCachedFrameCount = () => typeof window !== 'undefined' && window.innerWidth < 1024 ? SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MOBILE_MAX_CACHED_FRAME_COUNT : SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MAX_CACHED_FRAME_COUNT;
export const useThreesixtyLoader = (frames, enabled, t0) => {
  const $ = _c(78);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    getInitialFrameImages,
    isInteractiveBoosted: t2,
    isPreviewWarmupOnly: t3,
    mode: t4,
    paused: t5,
    requireCompleteSequence: t6
  } = options;
  const isInteractiveBoosted = t2 === undefined ? true : t2;
  const isPreviewWarmupOnly = t3 === undefined ? false : t3;
  const mode = t4 === undefined ? "interactive" : t4;
  const paused = t5 === undefined ? false : t5;
  const requireCompleteSequence = t6 === undefined ? false : t6;
  let t7;
  if ($[2] !== frames) {
    t7 = frames.join("\n");
    $[2] = frames;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const framesKey = t7;
  const [previewLoadState, setPreviewLoadState] = useState("loading");
  const [interactiveLoadState, setInteractiveLoadState] = useState("loading");
  let t8;
  if ($[4] !== requireCompleteSequence) {
    t8 = () => getCurrentFrameRequestWidth(requireCompleteSequence);
    $[4] = requireCompleteSequence;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const [frameRequestWidth, setFrameRequestWidth] = useState(t8);
  const [loadedFramesKey, setLoadedFramesKey] = useState(framesKey);
  let t9;
  if ($[6] !== frames.length || $[7] !== requireCompleteSequence) {
    t9 = requireCompleteSequence ? frames.length : getMaxCachedFrameCount();
    $[6] = frames.length;
    $[7] = requireCompleteSequence;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const maxCachedFrameCount = t9;
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = new Set();
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const readyRef = useRef(t10);
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = new Map();
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const cacheRef = useRef(t11);
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = new Map();
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const sharpCacheRef = useRef(t12);
  const sharpRequestControllerRef = useRef(null);
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = new Set();
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const fallbackReadyRef = useRef(t13);
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = new Map();
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  const fallbackCacheRef = useRef(t14);
  const fallbackMatchesFramesRef = useRef(false);
  const framesKeyRef = useRef(framesKey);
  let t15;
  if ($[14] !== frames.length || $[15] !== mode) {
    t15 = mode === "preview" ? getThreesixtyPreviewStartFrameIndex(frames.length) : 0;
    $[14] = frames.length;
    $[15] = mode;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  const currentIndexRef = useRef(t15);
  let t16;
  if ($[17] !== frames.length || $[18] !== mode) {
    t16 = mode === "preview" ? getThreesixtyPreviewStartFrameIndex(frames.length) : 0;
    $[17] = frames.length;
    $[18] = mode;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const displayedIndexRef = useRef(t16);
  const directionRef = useRef(0);
  const modeRef = useRef(mode);
  let t17;
  if ($[20] !== isInteractiveBoosted || $[21] !== requireCompleteSequence) {
    t17 = getInteractiveConcurrency(isInteractiveBoosted, requireCompleteSequence);
    $[20] = isInteractiveBoosted;
    $[21] = requireCompleteSequence;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  const interactiveConcurrencyRef = useRef(t17);
  const pausedRef = useRef(paused);
  const isPreviewWarmupOnlyRef = useRef(isPreviewWarmupOnly);
  const isPreviewReadyRef = useRef(false);
  const isInteractiveReadyRef = useRef(false);
  const pumpRef = useRef(_temp);
  const pauseLowPriorityRequestsRef = useRef(_temp2);
  const reprioritizeRef = useRef(_temp3);
  const retryRef = useRef(_temp4);
  let t18;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = new Set();
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  const subscribersRef = useRef(t18);
  let t19;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      sharpRequestControllerRef.current?.abort();
      sharpRequestControllerRef.current = null;
    };
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  const cancelSharpFrameRequest = t19;
  let t20;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      cancelSharpFrameRequest();
      sharpCacheRef.current = new Map();
      return cancelSharpFrameRequest;
    };
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  let t21;
  if ($[26] !== enabled || $[27] !== framesKey) {
    t21 = [cancelSharpFrameRequest, enabled, framesKey];
    $[26] = enabled;
    $[27] = framesKey;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[29] !== requireCompleteSequence) {
    t22 = () => {
      let timeoutId = null;
      const handleResize = () => {
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
        timeoutId = window.setTimeout(() => {
          const nextWidth = getCurrentFrameRequestWidth(requireCompleteSequence);
          setFrameRequestWidth(currentWidth => Math.max(currentWidth, nextWidth));
        }, 150);
      };
      window.addEventListener("resize", handleResize);
      handleResize();
      return () => {
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
        window.removeEventListener("resize", handleResize);
      };
    };
    t23 = [requireCompleteSequence];
    $[29] = requireCompleteSequence;
    $[30] = t22;
    $[31] = t23;
  } else {
    t22 = $[30];
    t23 = $[31];
  }
  useEffect(t22, t23);
  let t24;
  let t25;
  if ($[32] !== isInteractiveBoosted || $[33] !== isPreviewWarmupOnly || $[34] !== mode || $[35] !== paused || $[36] !== requireCompleteSequence) {
    t24 = () => {
      modeRef.current = mode;
      pausedRef.current = paused;
      isPreviewWarmupOnlyRef.current = isPreviewWarmupOnly;
      interactiveConcurrencyRef.current = getInteractiveConcurrency(isInteractiveBoosted, requireCompleteSequence);
      if (paused) {
        pauseLowPriorityRequestsRef.current();
      } else {
        pumpRef.current();
      }
    };
    t25 = [isInteractiveBoosted, isPreviewWarmupOnly, mode, paused, requireCompleteSequence];
    $[32] = isInteractiveBoosted;
    $[33] = isPreviewWarmupOnly;
    $[34] = mode;
    $[35] = paused;
    $[36] = requireCompleteSequence;
    $[37] = t24;
    $[38] = t25;
  } else {
    t24 = $[37];
    t25 = $[38];
  }
  useEffect(t24, t25);
  let t26;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => {
      if (modeRef.current === "preview") {
        isPreviewReadyRef.current = false;
        setPreviewLoadState("loading");
      } else {
        isInteractiveReadyRef.current = false;
        setInteractiveLoadState("loading");
      }
      retryRef.current();
    };
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  const retry = t26;
  let t27;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = callback => {
      subscribersRef.current.add(callback);
      return () => {
        subscribersRef.current.delete(callback);
      };
    };
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  const subscribeFrameReady = t27;
  let t28;
  let t29;
  if ($[41] !== enabled || $[42] !== frameRequestWidth || $[43] !== frames || $[44] !== framesKey || $[45] !== getInitialFrameImages || $[46] !== maxCachedFrameCount || $[47] !== requireCompleteSequence) {
    t28 = () => {
      const hasFrameSourceChanged = framesKeyRef.current !== framesKey;
      framesKeyRef.current = framesKey;
      setLoadedFramesKey(framesKey);
      if (!enabled || frames.length === 0) {
        readyRef.current = new Set();
        cacheRef.current = new Map();
        fallbackReadyRef.current = new Set();
        fallbackCacheRef.current = new Map();
        fallbackMatchesFramesRef.current = false;
        isPreviewReadyRef.current = false;
        isInteractiveReadyRef.current = false;
        setPreviewLoadState("loading");
        setInteractiveLoadState("loading");
        return;
      }
      const initialFrameCache = !hasFrameSourceChanged && cacheRef.current.size === 0 ? getInitialFrameImages?.() : undefined;
      const initialFrameImages = initialFrameCache?.requestWidth === frameRequestWidth ? initialFrameCache.frames : undefined;
      const hasFallbackFrames = !hasFrameSourceChanged && cacheRef.current.size > 0;
      fallbackReadyRef.current = hasFallbackFrames ? readyRef.current : new Set();
      fallbackCacheRef.current = hasFallbackFrames ? cacheRef.current : new Map();
      fallbackMatchesFramesRef.current = hasFallbackFrames && !hasFrameSourceChanged;
      readyRef.current = new Set(initialFrameImages?.keys());
      cacheRef.current = new Map(initialFrameImages);
      if (!hasFallbackFrames || hasFrameSourceChanged) {
        isPreviewReadyRef.current = false;
        isInteractiveReadyRef.current = false;
        setPreviewLoadState("loading");
        setInteractiveLoadState("loading");
      }
      if (hasFrameSourceChanged) {
        currentIndexRef.current = modeRef.current === "preview" ? getThreesixtyPreviewStartFrameIndex(frames.length) : 0;
        displayedIndexRef.current = currentIndexRef.current;
        directionRef.current = 0;
      }
      const controller = new AbortController();
      const previewIndexes = getThreesixtyPreviewFrameIndexes(frames.length);
      const previewPlaybackIndexes = getThreesixtyPreviewPlaybackFrameIndexes(frames.length);
      const inflight = new Set();
      const requestControllers = new Map();
      const requestPriorities = new Map();
      const attempts = new Map();
      const failedIndexes = new Set();
      let active = 0;
      let highPriorityActive = 0;
      let isActive = true;
      const canLoadIndex = index => modeRef.current === "interactive" || previewIndexes.has(index);
      const getIsReady = () => modeRef.current === "preview" ? isPreviewReadyRef.current : isInteractiveReadyRef.current;
      const setActiveLoadState = state => {
        if (modeRef.current === "preview") {
          setPreviewLoadState(state);
          return;
        }
        setInteractiveLoadState(state);
      };
      const pickNext = (t30, t31) => {
        const prefetchOnly = t30 === undefined ? false : t30;
        const playbackOnly = t31 === undefined ? false : t31;
        const center = currentIndexRef.current;
        const direction = directionRef.current;
        if (playbackOnly) {
          const endpointIndex = [center, 0].find(index_0 => canLoadIndex(index_0) && !readyRef.current.has(index_0) && !inflight.has(index_0) && !failedIndexes.has(index_0));
          if (endpointIndex !== undefined) {
            return endpointIndex;
          }
          if (isPreviewWarmupOnlyRef.current) {
            return -1;
          }
        }
        if (modeRef.current === "interactive" && direction !== 0 && displayedIndexRef.current !== center) {
          const distance = direction === 1 ? (center - displayedIndexRef.current + frames.length) % frames.length : (displayedIndexRef.current - center + frames.length) % frames.length;
          for (let offset = 1; offset <= distance; offset = offset + 1, offset) {
            const corridorIndex = wrapThreesixtyIndex(displayedIndexRef.current + direction * offset, frames.length);
            if (!readyRef.current.has(corridorIndex) && !inflight.has(corridorIndex) && !failedIndexes.has(corridorIndex)) {
              return corridorIndex;
            }
          }
        }
        let bestIndex = -1;
        let bestRank = Number.POSITIVE_INFINITY;
        for (let index_1 = 0; index_1 < frames.length; index_1 = index_1 + 1, index_1) {
          if (!canLoadIndex(index_1) || playbackOnly && !previewPlaybackIndexes.has(index_1) || readyRef.current.has(index_1) || inflight.has(index_1) || failedIndexes.has(index_1) || prefetchOnly && !isThreesixtyPrefetchFrame(index_1, center, direction, frames.length, maxCachedFrameCount)) {
            continue;
          }
          const rank = getThreesixtyLoadRank(index_1, center, direction, frames.length);
          if (rank < bestRank) {
            bestRank = rank;
            bestIndex = index_1;
          }
        }
        return bestIndex;
      };
      const hasActivePrefetchCoverage = () => {
        for (let index_2 = 0; index_2 < frames.length; index_2 = index_2 + 1, index_2) {
          if (canLoadIndex(index_2) && isThreesixtyPrefetchFrame(index_2, currentIndexRef.current, directionRef.current, frames.length, maxCachedFrameCount) && !readyRef.current.has(index_2)) {
            return false;
          }
        }
        return true;
      };
      const pump = () => {
        if (controller.signal.aborted || pausedRef.current) {
          return;
        }
        const isIdleInteractivePrefetch = modeRef.current === "interactive" && getIsReady() && directionRef.current === 0;
        const concurrency = modeRef.current === "preview" ? isPreviewWarmupOnlyRef.current ? 1 : SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_PREVIEW_CONCURRENCY : isIdleInteractivePrefetch ? SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_IDLE_CONCURRENCY : interactiveConcurrencyRef.current;
        while (active < concurrency) {
          const isReady = getIsReady();
          const playbackOnly_0 = modeRef.current === "preview" && !isReady;
          const nextIndex = pickNext(isReady, playbackOnly_0);
          if (nextIndex < 0) {
            if (active === 0 && !isReady && !(modeRef.current === "preview" && isPreviewWarmupOnlyRef.current)) {
              setActiveLoadState("error");
            }
            return;
          }
          const rank_0 = getThreesixtyLoadRank(nextIndex, currentIndexRef.current, directionRef.current, frames.length);
          const isHighPriority = !isPreviewWarmupOnlyRef.current && rank_0 <= 2 && highPriorityActive < SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MAX_HIGH_PRIORITY_REQUESTS;
          const attemptCount = (attempts.get(nextIndex) ?? 0) + 1;
          active = active + 1;
          active;
          if (isHighPriority) {
            highPriorityActive = highPriorityActive + 1;
            highPriorityActive;
          }
          inflight.add(nextIndex);
          attempts.set(nextIndex, attemptCount);
          const requestController = new AbortController();
          requestControllers.set(nextIndex, requestController);
          requestPriorities.set(nextIndex, isHighPriority ? "high" : "low");
          loadThreesixtyFrame(frames[nextIndex], requestController.signal, isHighPriority ? "high" : "low", frameRequestWidth, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_INTERACTIVE_FRAME_QUALITY).then(image => {
            if (controller.signal.aborted || requestController.signal.aborted) {
              return;
            }
            cacheRef.current.set(nextIndex, image);
            const nextReady = new Set(readyRef.current);
            nextReady.add(nextIndex);
            const protectedIndexes = new Set([0, currentIndexRef.current, displayedIndexRef.current]);
            if (directionRef.current !== 0) {
              const distance_0 = directionRef.current === 1 ? (currentIndexRef.current - displayedIndexRef.current + frames.length) % frames.length : (displayedIndexRef.current - currentIndexRef.current + frames.length) % frames.length;
              for (let offset_0 = 1; offset_0 <= distance_0 && protectedIndexes.size < maxCachedFrameCount; offset_0 = offset_0 + 1, offset_0) {
                protectedIndexes.add(wrapThreesixtyIndex(displayedIndexRef.current + directionRef.current * offset_0, frames.length));
              }
            }
            readyRef.current = trimThreesixtyFrameCache(cacheRef.current, nextReady, protectedIndexes, maxCachedFrameCount);
            subscribersRef.current.forEach(_temp5);
            if (!isPreviewReadyRef.current && hasReadyPreviewFrames(currentIndexRef.current, nextReady, previewPlaybackIndexes)) {
              isPreviewReadyRef.current = true;
              setPreviewLoadState("ready");
            }
            if (!isInteractiveReadyRef.current && (requireCompleteSequence ? nextReady.size === frames.length : hasReadyFrameWindow(0, nextReady, frames.length))) {
              isInteractiveReadyRef.current = true;
              setInteractiveLoadState("ready");
            }
            const isActiveModeReady = modeRef.current === "preview" ? isPreviewReadyRef.current : isInteractiveReadyRef.current;
            if (hasFrameSourceChanged && isActiveModeReady || fallbackMatchesFramesRef.current && hasActivePrefetchCoverage()) {
              fallbackReadyRef.current = new Set();
              fallbackCacheRef.current = new Map();
              fallbackMatchesFramesRef.current = false;
            }
          }).catch(error => {
            if (isThreesixtyAbortError(error)) {
              return;
            }
            if (attemptCount > SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_MAX_RETRIES) {
              failedIndexes.add(nextIndex);
            }
          }).finally(() => {
            if (!isActive) {
              return;
            }
            active = active - 1;
            active;
            if (isHighPriority) {
              highPriorityActive = highPriorityActive - 1;
              highPriorityActive;
            }
            inflight.delete(nextIndex);
            requestControllers.delete(nextIndex);
            requestPriorities.delete(nextIndex);
            pump();
          });
        }
      };
      reprioritizeRef.current = () => {
        if (modeRef.current !== "interactive") {
          return;
        }
        inflight.forEach(index_3 => {
          if (directionRef.current !== 0 && displayedIndexRef.current !== currentIndexRef.current) {
            const targetDistance = directionRef.current === 1 ? (currentIndexRef.current - displayedIndexRef.current + frames.length) % frames.length : (displayedIndexRef.current - currentIndexRef.current + frames.length) % frames.length;
            const indexDistance = directionRef.current === 1 ? (index_3 - displayedIndexRef.current + frames.length) % frames.length : (displayedIndexRef.current - index_3 + frames.length) % frames.length;
            if (indexDistance > 0 && indexDistance <= targetDistance) {
              return;
            }
          }
          if (isThreesixtyPrefetchFrame(index_3, currentIndexRef.current, directionRef.current, frames.length, maxCachedFrameCount)) {
            return;
          }
          attempts.delete(index_3);
          requestControllers.get(index_3)?.abort();
        });
      };
      retryRef.current = () => {
        failedIndexes.clear();
        attempts.clear();
        pump();
      };
      pauseLowPriorityRequestsRef.current = () => {
        requestControllers.forEach((requestController_0, index_4) => {
          if (requestPriorities.get(index_4) !== "low") {
            return;
          }
          attempts.delete(index_4);
          requestController_0.abort();
        });
      };
      pumpRef.current = pump;
      pump();
      return () => {
        isActive = false;
        controller.abort();
        requestControllers.forEach(_temp6);
        pumpRef.current = _temp7;
        pauseLowPriorityRequestsRef.current = _temp8;
        reprioritizeRef.current = _temp9;
        retryRef.current = _temp10;
      };
    };
    t29 = [enabled, frameRequestWidth, frames, framesKey, getInitialFrameImages, maxCachedFrameCount, requireCompleteSequence];
    $[41] = enabled;
    $[42] = frameRequestWidth;
    $[43] = frames;
    $[44] = framesKey;
    $[45] = getInitialFrameImages;
    $[46] = maxCachedFrameCount;
    $[47] = requireCompleteSequence;
    $[48] = t28;
    $[49] = t29;
  } else {
    t28 = $[48];
    t29 = $[49];
  }
  useEffect(t28, t29);
  const hasPendingFrameSourceChange = loadedFramesKey !== framesKey;
  const loadState = hasPendingFrameSourceChange ? "loading" : mode === "preview" ? previewLoadState : interactiveLoadState;
  let t30;
  let t31;
  if ($[50] !== enabled || $[51] !== isPreviewWarmupOnly || $[52] !== loadState || $[53] !== mode || $[54] !== requireCompleteSequence) {
    t30 = () => {
      if (!enabled || loadState !== "loading" || mode === "preview" && isPreviewWarmupOnly) {
        return;
      }
      const timeoutId_0 = window.setTimeout(() => {
        if (mode === "preview" && !isPreviewReadyRef.current) {
          setPreviewLoadState("error");
        }
        if (mode === "interactive" && !isInteractiveReadyRef.current) {
          setInteractiveLoadState("error");
        }
      }, requireCompleteSequence ? SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_COMPLETE_LOAD_TIMEOUT_MS : SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_LOAD_TIMEOUT_MS);
      return () => {
        window.clearTimeout(timeoutId_0);
      };
    };
    t31 = [enabled, isPreviewWarmupOnly, loadState, mode, requireCompleteSequence];
    $[50] = enabled;
    $[51] = isPreviewWarmupOnly;
    $[52] = loadState;
    $[53] = mode;
    $[54] = requireCompleteSequence;
    $[55] = t30;
    $[56] = t31;
  } else {
    t30 = $[55];
    t31 = $[56];
  }
  useEffect(t30, t31);
  let t32;
  if ($[57] !== frames.length) {
    t32 = (index_5, direction_0) => {
      const previousDirection = directionRef.current;
      currentIndexRef.current = wrapThreesixtyIndex(index_5, frames.length);
      directionRef.current = direction_0;
      if (direction_0 !== 0 && direction_0 !== previousDirection) {
        reprioritizeRef.current();
      }
      pumpRef.current();
    };
    $[57] = frames.length;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  const setCurrentIndex = t32;
  let t33;
  if ($[59] !== enabled || $[60] !== frames || $[61] !== requireCompleteSequence) {
    t33 = index_6 => {
      if (!enabled || !requireCompleteSequence || !isInteractiveReadyRef.current || frames.length === 0 || typeof window === "undefined" || window.innerWidth < 1024) {
        return;
      }
      const nextIndex_0 = wrapThreesixtyIndex(index_6, frames.length);
      const requestWidth = getThreesixtyStoppedFrameRequestWidth(window.innerWidth, window.devicePixelRatio);
      const cachedSharpFrame = sharpCacheRef.current.get(nextIndex_0);
      if (cachedSharpFrame && cachedSharpFrame.width >= requestWidth) {
        subscribersRef.current.forEach(_temp11);
        return;
      }
      cancelSharpFrameRequest();
      const requestController_2 = new AbortController();
      sharpRequestControllerRef.current = requestController_2;
      loadThreesixtyFrame(frames[nextIndex_0], requestController_2.signal, "high", requestWidth, SPARTAN_PRODUCT_EXTERIOR_THREESIXTY_SHARP_FRAME_QUALITY).then(image_0 => {
        if (requestController_2.signal.aborted || sharpRequestControllerRef.current !== requestController_2) {
          return;
        }
        sharpCacheRef.current = new Map([[nextIndex_0, {
          image: image_0,
          width: requestWidth
        }]]);
        sharpRequestControllerRef.current = null;
        subscribersRef.current.forEach(_temp12);
      }).catch(error_0 => {
        if (!isThreesixtyAbortError(error_0) && sharpRequestControllerRef.current === requestController_2) {
          sharpRequestControllerRef.current = null;
        }
      }).finally(() => {
        if (sharpRequestControllerRef.current === requestController_2) {
          sharpRequestControllerRef.current = null;
        }
      });
    };
    $[59] = enabled;
    $[60] = frames;
    $[61] = requireCompleteSequence;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  const requestSharpFrame = t33;
  let t34;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = index_7 => {
      const sharpImage = sharpCacheRef.current.get(index_7)?.image;
      if (sharpImage) {
        return sharpImage;
      }
      const image_1 = cacheRef.current.get(index_7);
      if (image_1) {
        cacheRef.current.delete(index_7);
        cacheRef.current.set(index_7, image_1);
        return image_1;
      }
      return fallbackMatchesFramesRef.current ? fallbackCacheRef.current.get(index_7) ?? null : null;
    };
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  const getFrameImage = t34;
  let t35;
  if ($[64] !== frames.length) {
    t35 = index_8 => {
      displayedIndexRef.current = wrapThreesixtyIndex(index_8, frames.length);
      pumpRef.current();
    };
    $[64] = frames.length;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  const setDisplayedIndex = t35;
  let t36;
  if ($[66] !== frames.length) {
    t36 = index_9 => {
      const maximumDistance = modeRef.current === "interactive" && directionRef.current !== 0 ? frames.length - 1 : undefined;
      const nearestIndex = getNearestReadyIndex(index_9, readyRef.current, frames.length, directionRef.current, maximumDistance);
      if (nearestIndex !== null && cacheRef.current.has(nearestIndex)) {
        return nearestIndex;
      }
      const fallbackIndex = getNearestReadyIndex(index_9, fallbackReadyRef.current, frames.length, directionRef.current, maximumDistance);
      return fallbackIndex !== null && fallbackCacheRef.current.has(fallbackIndex) ? fallbackIndex : null;
    };
    $[66] = frames.length;
    $[67] = t36;
  } else {
    t36 = $[67];
  }
  const getNearestFrameIndex = t36;
  let t37;
  if ($[68] !== getNearestFrameIndex) {
    t37 = index_10 => {
      const nearestIndex_0 = getNearestFrameIndex(index_10);
      if (nearestIndex_0 === null) {
        return null;
      }
      const image_2 = cacheRef.current.get(nearestIndex_0);
      if (image_2) {
        cacheRef.current.delete(nearestIndex_0);
        cacheRef.current.set(nearestIndex_0, image_2);
        return image_2;
      }
      return fallbackCacheRef.current.get(nearestIndex_0) ?? null;
    };
    $[68] = getNearestFrameIndex;
    $[69] = t37;
  } else {
    t37 = $[69];
  }
  const getNearestFrameImage = t37;
  const t38 = !hasPendingFrameSourceChange && loadState === "ready";
  let t39;
  if ($[70] !== getNearestFrameImage || $[71] !== getNearestFrameIndex || $[72] !== loadState || $[73] !== requestSharpFrame || $[74] !== setCurrentIndex || $[75] !== setDisplayedIndex || $[76] !== t38) {
    t39 = {
      isInteractionReady: t38,
      loadState,
      retry,
      setCurrentIndex,
      setDisplayedIndex,
      requestSharpFrame,
      cancelSharpFrameRequest,
      getFrameImage,
      getNearestFrameImage,
      getNearestFrameIndex,
      subscribeFrameReady
    };
    $[70] = getNearestFrameImage;
    $[71] = getNearestFrameIndex;
    $[72] = loadState;
    $[73] = requestSharpFrame;
    $[74] = setCurrentIndex;
    $[75] = setDisplayedIndex;
    $[76] = t38;
    $[77] = t39;
  } else {
    t39 = $[77];
  }
  return t39;
};
function _temp() {}
function _temp2() {}
function _temp3() {}
function _temp4() {}
function _temp5(callback_0) {
  return callback_0();
}
function _temp6(requestController_1) {
  return requestController_1.abort();
}
function _temp7() {}
function _temp8() {}
function _temp9() {}
function _temp10() {}
function _temp11(callback_1) {
  return callback_1();
}
function _temp12(callback_2) {
  return callback_2();
}